/**
 * PricingConfigPage — `/settings/pricing-config`.
 *
 * Full CRUD surface on top of service-pricing:
 *
 *   - GET    /v1/pricing/tariffs              — list (was already wired)
 *   - POST   /v1/pricing/tariffs              — create  (B slice CRUD)
 *   - PATCH  /v1/pricing/tariffs/:id          — update  (B slice CRUD)
 *   - DELETE /v1/pricing/tariffs/:id          — delete  (B slice CRUD)
 *
 * Zone codes + service class codes come from
 *   - GET /v1/pricing/zones
 *   - GET /v1/pricing/service-classes
 * so the create/edit form uses dropdowns sourced from real data.
 */
import { type JSX, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreditCard, Pencil, Plus, Trash2, X } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  createPricingTariff,
  deletePricingTariff,
  listPricingServiceClasses,
  listPricingTariffs,
  listPricingZones,
  type CreatePricingTariffInput,
  type PricingTariff,
  updatePricingTariff,
} from '../../lib/api/settings';
import { PageHeader } from '../shared/StaffUI';

const TARIFFS_KEY = ['pricing', 'tariffs'] as const;
const ZONES_KEY = ['pricing', 'zones'] as const;
const SERVICE_CLASSES_KEY = ['pricing', 'service-classes'] as const;

function formatNgn(minor: string): string {
  // eslint-disable-next-line upmp/no-number-money -- display formatter; the canonical value lives on the row.
  const n = parseFloat(minor);
  if (isNaN(n)) return minor;
  return `₦${n.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function groupTariffs(
  tariffs: readonly PricingTariff[],
): Record<string, PricingTariff[]> {
  const groups: Record<string, PricingTariff[]> = {};
  for (const t of tariffs) {
    const key = `${t.serviceClassCode} — ${t.originZoneCode} → ${t.destZoneCode}`;
    (groups[key] ??= []).push(t);
  }
  return groups;
}

/** Empty form template — used when opening "New tariff". */
function blankDraft(): CreatePricingTariffInput {
  return {
    originZoneCode: '',
    destZoneCode: '',
    serviceClassCode: '',
    weightCapGrams: 500,
    basePrice: '0.00',
    currency: 'NGN',
    perGramSurcharge: '0.0000',
    validFrom: new Date().toISOString(),
    validUntil: null,
  };
}

function rowToDraft(r: PricingTariff): CreatePricingTariffInput {
  return {
    originZoneCode: r.originZoneCode,
    destZoneCode: r.destZoneCode,
    serviceClassCode: r.serviceClassCode,
    weightCapGrams: r.weightCapGrams,
    basePrice: r.basePrice,
    currency: r.currency,
    perGramSurcharge: r.perGramSurcharge,
    validFrom: r.validFrom,
    validUntil: r.validUntil,
  };
}

export function PricingConfigPage(): JSX.Element {
  const qc = useQueryClient();

  const tariffs = useQuery({
    queryKey: TARIFFS_KEY,
    queryFn: () => listPricingTariffs(),
    retry: false,
    staleTime: 60_000,
  });

  const zones = useQuery({
    queryKey: ZONES_KEY,
    queryFn: () => listPricingZones(),
    retry: false,
    staleTime: 5 * 60_000,
  });

  const serviceClasses = useQuery({
    queryKey: SERVICE_CLASSES_KEY,
    queryFn: () => listPricingServiceClasses(),
    retry: false,
    staleTime: 5 * 60_000,
  });

  const denied =
    tariffs.isError &&
    tariffs.error instanceof ApiError &&
    (tariffs.error.status === 401 || tariffs.error.status === 403);

  const [modal, setModal] = useState<
    | null
    | { mode: 'create'; draft: CreatePricingTariffInput }
    | { mode: 'edit'; id: string; draft: CreatePricingTariffInput }
  >(null);
  const [confirmDelete, setConfirmDelete] = useState<PricingTariff | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: (input: CreatePricingTariffInput) => createPricingTariff(input),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: TARIFFS_KEY });
      setModal(null);
      setModalError(null);
    },
    onError: (e) => setModalError(e instanceof Error ? e.message : 'Create failed'),
  });

  const updateMut = useMutation({
    mutationFn: (args: { id: string; patch: Partial<CreatePricingTariffInput> }) =>
      updatePricingTariff(args.id, args.patch),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: TARIFFS_KEY });
      setModal(null);
      setModalError(null);
    },
    onError: (e) => setModalError(e instanceof Error ? e.message : 'Update failed'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deletePricingTariff(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: TARIFFS_KEY });
      setConfirmDelete(null);
    },
  });

  function submitModal(): void {
    if (!modal) return;
    if (modal.mode === 'create') {
      createMut.mutate(modal.draft);
    } else {
      updateMut.mutate({ id: modal.id, patch: modal.draft });
    }
  }

  const groups = groupTariffs(tariffs.data?.items ?? []);
  const groupKeys = Object.keys(groups);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Service pricing"
        description="Pricing tariff configuration by service class, origin zone and destination zone."
        actions={
          <div className="flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-muted" />
            <button
              type="button"
              onClick={() => {
                setModalError(null);
                setModal({ mode: 'create', draft: blankDraft() });
              }}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-nipost-green text-white rounded-lg text-sm font-medium"
            >
              <Plus className="w-4 h-4" /> New tariff
            </button>
          </div>
        }
      />

      {tariffs.isPending ? (
        <p className="text-sm text-muted">Loading tariffs…</p>
      ) : denied ? (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
          <p className="font-semibold">Permission required</p>
          <p>The token does not carry <code className="font-mono">Settings/managePricingConfig</code>.</p>
        </div>
      ) : tariffs.isError ? (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900">
          <p className="font-semibold">Tariff service unreachable</p>
          <p>{(tariffs.error as Error).message}</p>
        </div>
      ) : groupKeys.length === 0 ? (
        <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
          No tariff rows found.
        </p>
      ) : (
        <div className="space-y-4">
          {groupKeys.map((label) => {
            const rows = groups[label] ?? [];
            return (
              <div key={label} className="bg-white border border-border rounded-xl overflow-x-auto">
                <div className="px-4 py-3 border-b border-border bg-gray-50">
                  <h3 className="text-xs font-semibold text-gray-700">{label}</h3>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-border">
                    <tr>
                      <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">Weight cap (g)</th>
                      <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">Base price</th>
                      <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">Per-gram surcharge</th>
                      <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">Currency</th>
                      <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">Valid from</th>
                      <th className="px-4 py-2 text-right text-[10px] uppercase text-muted">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {rows.map((r) => (
                      <tr key={r.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-xs font-mono">{r.weightCapGrams.toLocaleString()}</td>
                        <td className="px-4 py-2 text-xs font-mono text-nipost-green">{formatNgn(r.basePrice)}</td>
                        <td className="px-4 py-2 text-xs font-mono">{r.perGramSurcharge}</td>
                        <td className="px-4 py-2 text-xs">{r.currency}</td>
                        <td className="px-4 py-2 text-xs text-muted">
                          {new Date(r.validFrom).toLocaleDateString('en-NG')}
                        </td>
                        <td className="px-4 py-2 text-right whitespace-nowrap">
                          <button
                            type="button"
                            aria-label={`Edit tariff ${r.id}`}
                            onClick={() => {
                              setModalError(null);
                              setModal({ mode: 'edit', id: r.id, draft: rowToDraft(r) });
                            }}
                            className="text-gray-600 hover:bg-gray-100 rounded p-1 mr-1"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            aria-label={`Delete tariff ${r.id}`}
                            onClick={() => setConfirmDelete(r)}
                            className="text-red-600 hover:bg-red-50 rounded p-1"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}

      {modal ? (
        <TariffModal
          mode={modal.mode}
          draft={modal.draft}
          zoneOptions={zones.data?.items ?? []}
          serviceClassOptions={serviceClasses.data?.items ?? []}
          onChange={(next) => setModal((m) => (m ? { ...m, draft: next } : m))}
          onCancel={() => {
            setModal(null);
            setModalError(null);
          }}
          onSubmit={submitModal}
          error={modalError}
          submitting={createMut.isPending || updateMut.isPending}
        />
      ) : null}

      {confirmDelete ? (
        <ConfirmDeleteModal
          tariff={confirmDelete}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => deleteMut.mutate(confirmDelete.id)}
          submitting={deleteMut.isPending}
          error={deleteMut.error instanceof Error ? deleteMut.error.message : null}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

interface TariffModalProps {
  readonly mode: 'create' | 'edit';
  readonly draft: CreatePricingTariffInput;
  readonly zoneOptions: readonly { code: string; name: string }[];
  readonly serviceClassOptions: readonly { code: string; displayName: string }[];
  readonly onChange: (next: CreatePricingTariffInput) => void;
  readonly onCancel: () => void;
  readonly onSubmit: () => void;
  readonly error: string | null;
  readonly submitting: boolean;
}

function TariffModal(props: TariffModalProps): JSX.Element {
  const { draft, onChange } = props;
  // The HTML datetime-local input wants "YYYY-MM-DDTHH:mm" — strip seconds/zone.
  const validFromLocal = draft.validFrom ? draft.validFrom.slice(0, 16) : '';
  const validUntilLocal = draft.validUntil ? draft.validUntil.slice(0, 16) : '';

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl border border-border w-full max-w-xl">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <p className="text-base font-bold text-gray-900">
            {props.mode === 'create' ? 'New tariff' : 'Edit tariff'}
          </p>
          <button
            type="button"
            onClick={props.onCancel}
            className="p-1.5 rounded hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="tariff-origin" className="text-[10px] font-semibold uppercase text-muted">Origin zone</label>
            <select
              id="tariff-origin"
              aria-label="Origin zone"
              value={draft.originZoneCode}
              onChange={(e) => onChange({ ...draft, originZoneCode: e.target.value })}
              className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg"
            >
              <option value="">Select…</option>
              {props.zoneOptions.map((z) => (
                <option key={z.code} value={z.code}>
                  {z.code} — {z.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="tariff-dest" className="text-[10px] font-semibold uppercase text-muted">Destination zone</label>
            <select
              id="tariff-dest"
              aria-label="Destination zone"
              value={draft.destZoneCode}
              onChange={(e) => onChange({ ...draft, destZoneCode: e.target.value })}
              className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg"
            >
              <option value="">Select…</option>
              {props.zoneOptions.map((z) => (
                <option key={z.code} value={z.code}>
                  {z.code} — {z.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="tariff-class" className="text-[10px] font-semibold uppercase text-muted">Service class</label>
            <select
              id="tariff-class"
              aria-label="Service class"
              value={draft.serviceClassCode}
              onChange={(e) => onChange({ ...draft, serviceClassCode: e.target.value })}
              className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg"
            >
              <option value="">Select…</option>
              {props.serviceClassOptions.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.code} — {s.displayName}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="tariff-cap" className="text-[10px] font-semibold uppercase text-muted">Weight cap (g)</label>
            <input
              id="tariff-cap"
              type="number"
              min={1}
              aria-label="Weight cap (g)"
              value={draft.weightCapGrams}
              onChange={(e) => onChange({ ...draft, weightCapGrams: Number(e.target.value) })}
              className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg font-mono"
            />
          </div>
          <div>
            <label htmlFor="tariff-base" className="text-[10px] font-semibold uppercase text-muted">Base price (2dp)</label>
            <input
              id="tariff-base"
              type="text"
              inputMode="decimal"
              aria-label="Base price"
              value={draft.basePrice}
              onChange={(e) => onChange({ ...draft, basePrice: e.target.value })}
              className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg font-mono"
            />
          </div>
          <div>
            <label htmlFor="tariff-currency" className="text-[10px] font-semibold uppercase text-muted">Currency</label>
            <input
              id="tariff-currency"
              type="text"
              maxLength={3}
              aria-label="Currency"
              value={draft.currency}
              onChange={(e) => onChange({ ...draft, currency: e.target.value.toUpperCase() })}
              className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg font-mono uppercase"
            />
          </div>
          <div>
            <label htmlFor="tariff-pergram" className="text-[10px] font-semibold uppercase text-muted">Per-gram surcharge</label>
            <input
              id="tariff-pergram"
              type="text"
              inputMode="decimal"
              aria-label="Per-gram surcharge"
              value={draft.perGramSurcharge}
              onChange={(e) => onChange({ ...draft, perGramSurcharge: e.target.value })}
              className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg font-mono"
            />
          </div>
          <div>
            <label htmlFor="tariff-validfrom" className="text-[10px] font-semibold uppercase text-muted">Valid from</label>
            <input
              id="tariff-validfrom"
              type="datetime-local"
              aria-label="Valid from"
              value={validFromLocal}
              onChange={(e) => {
                const iso = e.target.value ? new Date(e.target.value).toISOString() : '';
                onChange({ ...draft, validFrom: iso });
              }}
              className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg"
            />
          </div>
          <div className="col-span-2">
            <label htmlFor="tariff-validuntil" className="text-[10px] font-semibold uppercase text-muted">Valid until (optional)</label>
            <input
              id="tariff-validuntil"
              type="datetime-local"
              aria-label="Valid until"
              value={validUntilLocal}
              onChange={(e) => {
                const iso = e.target.value ? new Date(e.target.value).toISOString() : null;
                onChange({ ...draft, validUntil: iso });
              }}
              className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg"
            />
          </div>
        </div>
        {props.error ? (
          <div className="px-4 pb-2 text-xs text-red-600" role="alert">{props.error}</div>
        ) : null}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
          <button
            type="button"
            onClick={props.onCancel}
            className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={props.onSubmit}
            disabled={props.submitting}
            className="px-3 py-1.5 text-sm bg-nipost-green text-white rounded-lg disabled:opacity-60"
          >
            {props.submitting
              ? 'Saving…'
              : props.mode === 'create' ? 'Create tariff' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface ConfirmDeleteModalProps {
  readonly tariff: PricingTariff;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
  readonly submitting: boolean;
  readonly error: string | null;
}

function ConfirmDeleteModal(props: ConfirmDeleteModalProps): JSX.Element {
  const t = props.tariff;
  const summary = `Delete tariff for ${t.serviceClassCode} on ${t.originZoneCode} → ${t.destZoneCode} ${t.weightCapGrams}g?`;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl border border-border w-full max-w-md">
        <div className="p-4 border-b border-border">
          <p className="text-base font-bold text-gray-900">Confirm deletion</p>
        </div>
        <div className="p-4 text-sm text-gray-700 space-y-2">
          <p>{summary}</p>
          <p className="text-xs text-muted">This action is logged via the FinancialAudit trail and cannot be reversed.</p>
        </div>
        {props.error ? (
          <div className="px-4 pb-2 text-xs text-red-600" role="alert">{props.error}</div>
        ) : null}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
          <button
            type="button"
            onClick={props.onCancel}
            className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={props.onConfirm}
            disabled={props.submitting}
            className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg disabled:opacity-60"
          >
            {props.submitting ? 'Deleting…' : 'Delete tariff'}
          </button>
        </div>
      </div>
    </div>
  );
}
